import { consola } from 'consola'
import { ref, computed } from 'vue'
import { useModalStore , modalStateEnum } from '@shared/model'
import { usePaymentStore , useRetryPayment } from '@features/payment'
import { useInstitutionStore } from '@entities/institution'
import { useUserProfileStore } from '@entities/user'


import { getPaymentUrl } from '@entities/order'

/**
 * Composable для логики модального окна заказа
 * ✅ РЕФАКТОРИНГ: Убрана локальная копия order, используется напрямую selectedOrder
 */
export function useOrderModal() {
  const modalStore = useModalStore()
  const paymentStore = usePaymentStore()
  const institutionStore = useInstitutionStore()
  const userProfileStore = useUserProfileStore()

  // ✅ ОБНОВЛЕНО: Используем selectedOrder напрямую, без локальной копии
  const {
    selectedOrderId,
    unpaidOrders,
    unpaidOrdersCount,
    canRetryPayment,
    formattedTimeRemaining,
    initiateRetryPayment: _initiateRetryPayment,
    updateOrderStatus,
    loadOrderDetails,
    isLoading: isRetryingPayment,
    isLoadingDetails,
    clearUnpaidOrder,
    clearCompletedOrderStorage,
    selectOrder,
    unpaidOrdersStore,  // ✅ Берем сам store для прямого доступа
  } = useRetryPayment()

  // ✅ КРИТИЧЕСКИ ВАЖНО: Используем напрямую из store для реактивности
  const order = computed(() => unpaidOrdersStore.selectedOrder)

  // Состояния для polling
  const isLoadingOrder = ref(false)
  const orderError = ref<Error | null>(null)

  // Состояние загрузки для повторной оплаты
  const isInitiatingPayment = ref(false)

  let pollingTimer: ReturnType<typeof setInterval> | null = null

  /**
   * Получает ID текущего заказа из разных источников
   */
  const currentOrderId = computed(() => {
    const sources = [
      order.value?.id,
      modalStore.modalData?.orderId,
      modalStore.modalData?.order?.id,
      paymentStore.orderId,
    ]

    const id = sources.find(source => source != null)
    consola.debug('🔍 currentOrderId computed sources:', sources, 'selected:', id)
    return id
  })

  /**
   * Получает URL для оплаты
   */
  const paymentUrl = computed(() => {
    return getPaymentUrl(order.value, modalStore.modalData)
  })

  /**
   * Получает ID пользователя
   */
  const userId = computed(() => {
    return userProfileStore.getUserId
  })

  /**
   * Получает добавки из store институций
   */
  const addons = computed(() => {
    return institutionStore.addons || []
  })

  /**
   * Получает исключения из store институций
   */
  const excludes = computed(() => {
    return institutionStore.excludeIngredients || []
  })

  /**
   * Загружает addons и excludes если они не загружены
   */
  async function loadAddonsAndExcludes(): Promise<void> {
    if (addons.value.length === 0 || excludes.value.length === 0) {
      consola.debug('🚀 Loading addons and excludes from institution store...')
      try {
        const selectedBranch = institutionStore.selectedBranch
        if (selectedBranch && userId.value) {
          await institutionStore.fetchProducts(selectedBranch.id, userId.value)
          consola.debug('🚀 Addons and excludes loaded:', {
            addonsCount: addons.value.length,
            excludesCount: excludes.value.length,
          })
        }
      } catch (error) {
        consola.error('🚀 Failed to load addons and excludes:', error)
      }
    }
  }

  /**
   * Обновляет статус заказа
   */
  async function fetchOrderStatus(): Promise<void> {
    if (!currentOrderId.value || isLoadingOrder.value) return

    // ✅ ИСПРАВЛЕНИЕ: Проверяем, не истекло ли время на оплату
    if (!canRetryPayment.value && order.value?.paymentStatus === 'waiting') {
      consola.debug('⏰ [fetchOrderStatus] Payment time expired, stopping polling')
      stopPolling()
      return
    }

    consola.debug('🔄 fetchOrderStatus called for order:', currentOrderId.value)
    isLoadingOrder.value = true

    try {
      const statusData = await updateOrderStatus(currentOrderId.value)
      if (statusData) {
        orderError.value = null
        consola.debug('✅ Order status updated in modal:', statusData)

        // Если заказ завершен, очищаем только localStorage неоплаченных заказов
        if (statusData.status === 'delivered' || statusData.payment_status === 'paid') {
          consola.debug('🏁 Order is completed, cleaning up localStorage...')
          if (process.client) {
            localStorage.removeItem('currentOrder')
            localStorage.removeItem('unpaidOrder')
          }

          // Останавливаем polling для завершенных заказов
          consola.debug('🛑 Stopping polling for completed order')
          stopPolling()
        }
      } else {
        consola.warn('⚠️ No status data received from updateOrderStatus')
      }
    } catch (error: any) {
      orderError.value = error
      consola.warn('❌ Polling failed, but continuing...', error)
    } finally {
      isLoadingOrder.value = false
    }
  }

  /**
   * Запускает периодическое обновление статуса
   */
  function startPolling(): void {
    if (pollingTimer) return
    // Уменьшаем интервал до 30 секунд для более быстрого обновления статуса
    pollingTimer = setInterval(fetchOrderStatus, 30_000)
  }

  /**
   * Останавливает периодическое обновление статуса
   */
  function stopPolling(): void {
    if (pollingTimer) {
      clearInterval(pollingTimer)
      pollingTimer = null
    }
  }

  /**
   * Обрабатывает повторную оплату заказа - использует существующий payment URL
   * ✅ ИСПРАВЛЕНО: Не вызывает deprecated endpoint /payments/universal/initiate
   */
  function handleRetryPayment(): void {
    if (!canRetryPayment.value || isRetryingPayment.value || isInitiatingPayment.value) return

    consola.debug('🔄 [handleRetryPayment] Starting retry payment for order:', order.value?.id)

    if (!order.value?.id) {
      consola.error('❌ [handleRetryPayment] Missing order data')
      return
    }

    // Устанавливаем состояние загрузки
    isInitiatingPayment.value = true

    try {
      // ✅ Получаем payment URL из всех совместимых источников
      const existingPaymentUrl =
        getPaymentUrl(order.value, modalStore.modalData) ||
        getPaymentUrl(paymentStore.order, modalStore.modalData)

      if (!existingPaymentUrl) {
        consola.error('❌ [handleRetryPayment] No payment URL found in order:', order.value)
        throw new Error('Для этого заказа не найдена ссылка на оплату. Пожалуйста, создайте новый заказ.')
      }

      consola.debug('💳 [handleRetryPayment] Using existing payment URL:', {
        orderId: order.value.id,
        hasPaymentUrl: !!existingPaymentUrl,
        paymentStatus: order.value.paymentStatus,
      })

      // Сохраняем данные платежа в store
      paymentStore.setOrderId(Number(order.value.id))
      const orderAmount =
        order.value.totalAmount ||
        order.value.total_amount ||
        paymentStore.order?.totalAmount ||
        paymentStore.order?.total_amount
      if (orderAmount) {
        paymentStore.setAmount(Number(orderAmount))
      }
      paymentStore.setConfirmationUrl(existingPaymentUrl)

      // Закрываем текущую модалку
      modalStore.close()

      // ✅ Определяем провайдера на основе payment URL или active_payment_system
      const activePaymentSystem = institutionStore.institution?.active_payment_system
      const paymentProvider = order.value.paymentProvider || activePaymentSystem

      consola.debug('💳 [handleRetryPayment] Payment provider:', paymentProvider)

      // Открываем виджет в зависимости от провайдера
      if (paymentProvider === 'alfabank' || existingPaymentUrl.includes('alfabank')) {
        consola.debug('🏦 [handleRetryPayment] Opening AlfaBank widget')
        // URL возврата и признак тестового режима задаёт бэкенд при регистрации заказа
        // в шлюзе — на клиенте их не выдумываем.
        modalStore.open(modalStateEnum.AlfaBankWidget, {
          confirmationUrl: existingPaymentUrl,
          // paymentId — это mdOrder заказа в шлюзе (external_payment_id), а не наш id заказа:
          // по нему бэкенд сверяет платёж напрямую с Альфа-Банком
          paymentId: order.value.externalPaymentId,
          orderId: order.value.id,
        })
      } else if (paymentProvider === 'yookassa' || existingPaymentUrl.includes('yookassa') || existingPaymentUrl.includes('yoomoney')) {
        consola.debug('💳 [handleRetryPayment] Opening YooKassa payment')
        window.open(existingPaymentUrl, '_blank')
      } else if (paymentProvider === 'sberbank' || existingPaymentUrl.includes('sberbank')) {
        consola.debug('🏛️ [handleRetryPayment] Opening Sberbank payment')
        window.open(existingPaymentUrl, '_blank')
      } else if (paymentProvider === 'tbusiness' || existingPaymentUrl.includes('tbusiness')) {
        consola.debug('🏢 [handleRetryPayment] Opening T-Business widget')
        modalStore.open(modalStateEnum.TBusinessWidget)
      } else {
        // Fallback: открываем в новой вкладке
        consola.debug('🔗 [handleRetryPayment] Opening payment in new tab (unknown provider)')
        window.open(existingPaymentUrl, '_blank')
      }

      consola.debug('✅ [handleRetryPayment] Payment initiated successfully')
    } catch (error: any) {
      consola.error('❌ [handleRetryPayment] Failed to initiate payment:', error)

      // Показываем ошибку пользователю
      const errorMessage = error?.message || error?.data?.message || 'Не удалось открыть страницу оплаты'
      alert(`Ошибка при попытке оплаты: ${errorMessage}`)
    } finally {
      // Сбрасываем состояние загрузки
      isInitiatingPayment.value = false
    }
  }

  /**
   * Обрабатывает создание нового заказа
   */
  function handleCreateNewOrder(): void {
    modalStore.close()
  }

  /**
   * Обрабатывает фокус окна браузера
   */
  async function handleWindowFocus(): Promise<void> {
    consola.debug('Window focus detected, checking order status...')
    await fetchOrderStatus()
  }

  /**
   * ✅ ОПТИМИЗИРОВАНО: Инициализирует модальное окно и загружает полные данные заказа
   */
  async function initialize(): Promise<void> {
    consola.debug('🚀 [OrderModal.initialize] Starting initialization...')
    consola.debug('🚀 [OrderModal.initialize] currentOrderId:', currentOrderId.value)
    consola.debug('🚀 [OrderModal.initialize] selectedOrder BEFORE:', order.value)

    if (!currentOrderId.value) {
      consola.warn('⚠️ [OrderModal.initialize] No order ID available')
      return
    }

    // ✅ ГЛАВНАЯ ЛОГИКА: Загружаем полные данные если их еще нет
    try {
      const { useUnpaidOrdersStore } = await import('@features/payment')
      const unpaidOrdersStore = useUnpaidOrdersStore()

      // ✅ КРИТИЧЕСКИ ВАЖНО: Устанавливаем активный заказ в store
      consola.debug('📍 [OrderModal.initialize] Setting active order:', String(currentOrderId.value))

      // Проверяем, есть ли заказ в summaries
      const existingSummary = unpaidOrdersStore.getSummaryById(String(currentOrderId.value))

      if (existingSummary) {
        consola.debug('✅ [OrderModal.initialize] Order found in summaries')
        unpaidOrdersStore.selectOrder(String(currentOrderId.value))
      } else {
        consola.debug('⚠️ [OrderModal.initialize] Order not in summaries, creating summary from current data')

        // Создаем summary из доступных данных (modalData или paymentStore)
        const orderFromModal = modalStore.modalData?.order
        const orderFromPayment = paymentStore.order
        const orderFromState = orderFromModal || orderFromPayment

        consola.debug('🔍 [OrderModal.initialize] Order sources:', {
          fromModal: !!orderFromModal,
          fromPayment: !!orderFromPayment,
          fromState: !!orderFromState,
        })

        if (orderFromState) {
          unpaidOrdersStore.addSummary({
            id: String(currentOrderId.value),
            status: orderFromState.status || 'pending',
            paymentStatus: orderFromState.paymentStatus || orderFromState.payment_status || 'waiting',
            createdAt: orderFromState.createdAt || orderFromState.created_at || new Date().toISOString(),
            updatedAt: orderFromState.updatedAt || orderFromState.updated_at || new Date().toISOString(),
            externalPaymentId: orderFromState.externalPaymentId || orderFromState.external_payment_id,
          })
          consola.debug('✅ [OrderModal.initialize] Summary created and added to store')
        } else {
          consola.warn('⚠️ [OrderModal.initialize] No order data available to create summary')
          // Создаем минимальный summary с только ID
          unpaidOrdersStore.addSummary({
            id: String(currentOrderId.value),
            status: 'pending',
            paymentStatus: 'waiting',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            externalPaymentId: undefined,
          })
        }
      }

      consola.debug('🔍 [OrderModal.initialize] selectedOrder AFTER selectOrder:', order.value)
      consola.debug('🔍 [OrderModal.initialize] unpaidOrdersStore.selectedOrder:', unpaidOrdersStore.selectedOrder)
      consola.debug('🔍 [OrderModal.initialize] unpaidOrdersStore.selectedOrderId:', unpaidOrdersStore.selectedOrderId)

      // Проверяем, загружены ли уже полные данные для этого заказа
      if (unpaidOrdersStore.hasDetails(String(currentOrderId.value))) {
        consola.debug('💾 [OrderModal.initialize] Full order details already in cache')
      } else {
        consola.debug('📥 [OrderModal.initialize] Loading full order details from API...')
        await unpaidOrdersStore.loadOrderDetails(String(currentOrderId.value))
        consola.debug('✅ [OrderModal.initialize] Full order details loaded')
      }

      // Логируем результат
      consola.debug('✅ [OrderModal.initialize] Order data ready:', {
        orderId: order.value?.id,
        hasItems: !!order.value?.items,
        itemsCount: order.value?.items?.length,
        totalAmount: order.value?.totalAmount,
        status: order.value?.status,
        paymentStatus: order.value?.paymentStatus,
      })
    } catch (error) {
      consola.error('❌ [OrderModal.initialize] Failed to load order details:', error)
    }

    // Загружаем addons и excludes
    await loadAddonsAndExcludes()

    // Немедленно проверяем статус заказа при открытии модального окна
    await fetchOrderStatus()

    // Запускаем polling для периодических обновлений
    startPolling()

    // Добавляем обработчик для обновления статуса при возврате на вкладку (после оплаты)
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', handleWindowFocus)
    }
  }

  /**
   * Очищает ресурсы при размонтировании
   */
  function cleanup(): void {
    stopPolling()

    // Очищаем обработчик focus
    if (typeof window !== 'undefined') {
      window.removeEventListener('focus', handleWindowFocus)
    }
  }

  return {
    // State
    order,  // ✅ Теперь это selectedOrder (computed, всегда актуальный)
    currentOrderId,
    paymentUrl,
    userId,
    addons,
    excludes,
    canRetryPayment,
    formattedTimeRemaining,
    isRetryingPayment,
    isLoadingDetails,
    isLoadingOrder,
    isInitiatingPayment,  // ✅ НОВОЕ: Состояние загрузки для повторной оплаты
    orderError,

    // ✅ НОВОЕ: Состояния для множественных заказов
    unpaidOrders,        // Массив всех неоплаченных заказов
    unpaidOrdersCount,   // Количество неоплаченных заказов
    selectedOrderId,     // ID выбранного заказа

    // Actions
    handleRetryPayment,
    handleCreateNewOrder,
    clearUnpaidOrder,
    clearCompletedOrderStorage,
    loadOrderDetails,
    fetchOrderStatus,
    initialize,
    cleanup,

    // ✅ НОВОЕ: Действие для переключения заказов
    selectOrder,  // (orderId: string) => void - переключает активный заказ
  }
}


